/**
 * THE FIRE LIFE SAFETY CUT SHEET — everything on the record that the fire
 * alarm company needs to quote the job, on one sheet, built from the document
 * and nothing else (spec Amendment 6, D-027).
 *
 * ── Why it exists ────────────────────────────────────────────────────────────
 * The design authority, after the 2026-08-18 field test and a conversation with
 * the surveyor who runs these jobs:
 *
 *   *"Throughout the survey across the Electrical, Pit and Lobbies sections
 *    there are questions related to fire life safety scope. He wants to leave
 *    those questions where they are (not move them), but the final FLS screen
 *    will merely be a read-only 'cut sheet' of everything related to fire life
 *    safety on one screen … that agent will need to produce two survey PDFs —
 *    one for the whole job, and a separate one for the FLS company."*
 *
 * So the FLS questions stay where the surveyor is standing when they can answer
 * them — the relays in Electrical, the detectors in the pit and at the top of
 * the hoistway, the smokes and sprinklers on the lobby walk — and THIS is the
 * sheet that gathers them back up. It is read in two places and must say the
 * same thing in both: the Fire Life Safety screen, where the surveyor reads it
 * before closing the survey, and `SubmittedSurvey.cutSheets.fls`, where the
 * office's report agent reads it to print the FLS company's PDF.
 *
 * ── What it mirrors ──────────────────────────────────────────────────────────
 * The shape follows the FLS company's own RFQ template line for line, because
 * that is the form the quote comes back on:
 *
 *   Date · Job Name · Address · Number of Elevators · Number of Landings ·
 *   Number of Machine Rooms · Number of Hoistways · Existing Panel ·
 *   Existing Devices (Lobbies / Pit / Overhead / Machine Room: Smokes, Heats,
 *   Sprinkled) · Existing Relays in Machine Room (Primary / Alternate /
 *   Flashing Hat / Shunt) · Current FLS Service Provider · Photo Link ·
 *   Job Specific Notes
 *
 * `summary` is that grid. The per-entity arrays underneath it (`machineRooms`,
 * `hoistways`, `lobbyGroups`) are the same facts before they were folded — one
 * row per room, shaft and bank, so the company can see WHICH pit has the smoke
 * detector, not just that one does.
 *
 * ── What it is not ───────────────────────────────────────────────────────────
 * Not a second source of truth. Every value is read from `answers`, the entity
 * tree or `photos` at the moment it is built; nothing on the sheet is typed
 * into it. The device embeds one in the submitted JSON so the agent has a
 * ready-made block, and the office computes its own from the sealed document
 * with this same function — so the two can only differ if the document does.
 * `null` means "not on the record"; the sheet never invents a No.
 */

import { addressPartsOf, formatAddress } from './address';
import { QUESTIONS, byId } from './catalog';
import { flsBackBoxCount, isQuestionVisible } from './calc';
import { buildVendorForm, type VendorForm } from './flsVendorForm';
import { SHOT_LISTS, buildSurveySections, qualifyTitle, shotBaseId, shotCarId } from './sections';
import {
  answerKey,
  sectionInstanceId,
  type AnswerValue,
  type Contact,
  type MachineRoom,
  type PhotoRef,
  type Scope,
  type SurveyDocument,
  type WallRange,
} from './types';
import { missingShots, shotReasonFor } from './validation';

/**
 * Bumped when the SHAPE of the sheet changes; the schema version covers the
 * rest. 3 since round 9 (Amendment 8): the members the fire alarm company's
 * site survey asks for, and `vendorForm` (Task 7).
 */
export const FLS_CUT_SHEET_VERSION = 3 as const;

/**
 * The §3.11 id scheme, read as the catalog states it: one prefix **F**, then a
 * number, then the usual lowercase suffix for a follow-up (`F11a`). Matching on
 * the documented convention rather than listing the ids is what keeps a new
 * FLS question from arriving in the catalog and falling off this sheet with
 * nobody noticing. `tests/unit/fls-ui.test.ts` pins it against the catalog in
 * both directions.
 */
const FLS_QUESTION_ID = /^F\d/;

export function isFlsQuestionId(questionId: string): boolean {
  return FLS_QUESTION_ID.test(questionId);
}

/* ═══════════════════════════════ the shape ═══════════════════════════════ */

/** Yes / No / partly / nobody said — the grid never prints a No it was not given. */
export type Presence = 'yes' | 'no' | 'some' | 'unknown';

export interface CutSheetPhoto {
  id: string;
  r2Key: string;
  contentType: string;
  bytes: number;
  capturedAt: string;
  uploadState: PhotoRef['uploadState'];
  sectionInstanceId: string;
  /** Surveyor-facing title of the section the photo is filed under. */
  sectionTitle: string;
  questionId?: string;
  shotListId?: string;
  /**
   * What the picture is of. The surveyor's own caption (`PhotoRef.label`,
   * D-038) when they typed one, else the same catalog chain this always
   * read: the shot's label, the question's, or the section's title.
   */
  label: string;
  /**
   * The catalog's own prompt — the shot/question/section chain above —
   * always, even when `label` is the surveyor's own words instead. Round 8
   * task 5: the office's estimate agent wants both, not just whichever one
   * ended up in `label`.
   */
  prompt?: string;
  /** Amendment 8 (D-041): which car this photo is of, for a per-car shot. */
  carId?: string;
  /** The car's own label, e.g. "Car 1" — the company reads this, not the id. */
  carLabel?: string;
  /** Amendment 8 (D-041): copied from the section's shot list — Chip's ruling that a required blank costs a gap, not a note. */
  required?: true;
}

export interface CutSheetDevices {
  smoke: Presence;
  heat: Presence;
  sprinkled: Presence;
}

export interface CutSheetRoom {
  id: string;
  name: string;
  type: MachineRoom['type'];
  cars: string[];
  /** M4. */
  sprinklered: boolean | null;
  /** F6 — a heat detector within 18" of the sprinkler head. */
  heatDetectorAtSprinklerHead: boolean | null;
  /** F7. */
  shuntRelay: boolean | null;
  /** F8 — the relay inventory, in the template's words. `null` = not answered. */
  relays: { primary: boolean; alternate: boolean; flashingHat: boolean; shunt: boolean } | null;
  /** F14 — the detector inventory. `null` = not answered. */
  devices: { smoke: boolean; heat: boolean } | null;
  /** M5 — context only, no price attached. */
  location: string | null;
  /** F15 — feet from the FACP to this room; 0 means the panel is in the room. */
  facpDistanceFt: number | null;
  /** F16 — read off the car operating panel, one answer for every car in the room. */
  recallInstalled: 'yes' | 'no' | 'unknown' | null;
  /** F20 — only ever answered when M4 is Yes. */
  shuntDisconnectLocation: string | null;
  /** F21. */
  mainDisconnectFeed: string | null;
  photos: CutSheetPhoto[];
  /** Amendment 8 (D-041): the cab section's shots are per car — the COP and the posted certificate, one row per car. */
  carPhotos: { carId: string; carLabel: string; photos: CutSheetPhoto[] }[];
}

export interface CutSheetHoistway {
  id: string;
  name: string;
  roomId: string;
  roomName: string;
  cars: string[];
  overhead: {
    /** F9. */
    sprinklered: boolean | null;
    /** F13. */
    devices: { smoke: boolean; heat: boolean } | null;
  };
  pit: {
    /** F10. */
    devices: { sprinkler: boolean; heat: boolean; smoke: boolean } | null;
  };
  photos: CutSheetPhoto[];
}

export interface LandingSpan {
  fromLanding: number;
  toLanding: number;
}

export interface CutSheetLobbyGroup {
  id: string;
  name: string;
  roomId: string;
  roomName: string;
  cars: string[];
  landings: number;
  /** F11 / F11a. */
  smokeDetectorsMissing: boolean | null;
  landingsMissingSmoke: LandingSpan[];
  /** F12 / F12a. */
  sprinklered: boolean | null;
  sprinkleredLandings: LandingSpan[];
  /** F12b — only asked when F12 is Yes. */
  sprinkleredLobbiesHaveHeat: boolean | null;
  /** F17 — Smoke / Heat / Other at the landings. `null` = not answered. */
  detectorTypes: { smoke: boolean; heat: boolean; other: boolean } | null;
  photos: CutSheetPhoto[];
}

export interface CutSheetNote {
  sectionTitle: string;
  questionId?: string;
  text: string;
  createdAt: string;
}

/**
 * A blank on the sheet — an FLS question nobody answered, OR (Amendment 8,
 * D-041) a REQUIRED shot nobody took: exactly one of `questionId`/`shotListId`
 * is present, never both, never neither.
 */
export interface CutSheetOpenItem {
  questionId?: string;
  shotListId?: string;
  label: string;
  sectionInstanceId: string;
  sectionTitle: string;
  /** The surveyor's written reason for the missing required shot, if they gave one. */
  reason?: string;
}

export interface FlsCutSheet {
  template: 'fls-turnkey-rfq';
  version: typeof FLS_CUT_SHEET_VERSION;
  job: {
    name: string | null;
    /** `submittedByName` (who submitted), else `assignedTo` (who it is assigned to) — Amendment 8 (D-041). */
    surveyor: string | null;
    /** The one printed line, composed from B2 + B2a–c by `formatAddress` (D-039). */
    address: string | null;
    /** The parts beside it — null on a record that holds everything in B2. */
    city: string | null;
    state: string | null;
    zip: string | null;
    buildingType: string | null;
    /** `submittedAt` when sealed, else the document's last write. */
    date: string;
    elevators: number | null;
    landings: number;
    machineRooms: number;
    hoistways: number;
    groups: number;
  };
  panel: {
    /** F3. */
    makeModel: string | null;
    /** F4 — the template's "Current FLS Service Provider". */
    serviceProvider: string | null;
    /** F5. */
    serviceContact: Contact | null;
    /** F1 — one back box per landing, from the entity tree (derived), unless typed. */
    backBoxes: number | null;
    /** F2 — the template's "Job Specific Notes", the surveyor's own line to the company. */
    notes: string | null;
    /** F19 — how the fire alarm company gets into the building. Not asked since D-045 (2026-09-11); still read, so an older survey's answer prints. */
    access: string | null;
    photos: CutSheetPhoto[];
  };
  /** The template's "Existing Devices" grid, folded from the rows below. */
  summary: {
    /** F18 — a building-level flag no per-room/per-shaft Yes can be folded into (a building can be sprinklered somewhere nobody went). */
    sprinklerSystem: 'yes' | 'no' | 'unknown' | null;
    lobbies: CutSheetDevices;
    pit: CutSheetDevices;
    overhead: CutSheetDevices;
    machineRoom: CutSheetDevices;
    /** The template's "Existing Relays in Machine Room", any room. */
    relays: { primary: Presence; alternate: Presence; flashingHat: Presence; shunt: Presence };
    /** Required (code, from M4) vs. existing (F7/F8) — spares the company reading four cells to work it out themselves. */
    shunt: { required: Presence; existing: Presence };
  };
  machineRooms: CutSheetRoom[];
  hoistways: CutSheetHoistway[];
  lobbyGroups: CutSheetLobbyGroup[];
  /** Every photo above, flat, for the "Photo Link" line. */
  photos: CutSheetPhoto[];
  /** Notes attached to an FLS question, or to the FLS section itself. */
  notes: CutSheetNote[];
  /** FLS questions the form asked and nobody answered — the sheet's honest blanks. */
  open: CutSheetOpenItem[];
  /** The fire alarm company's own three-page site survey, field for field (spec §5.2, D-041, Task 7) — built from every member above, so it introduces no fact the rest of the sheet does not already carry. */
  vendorForm: VendorForm;
}

/* ═══════════════════════════════ reading ═════════════════════════════════ */

function answerOf(doc: SurveyDocument, id: string, scope: Scope = {}): AnswerValue | undefined {
  return doc.answers[answerKey(id, scope)];
}

function textOf(doc: SurveyDocument, id: string, scope: Scope = {}): string | null {
  const answer = answerOf(doc, id, scope);
  if (!answer) return null;
  if (answer.kind === 'text' || answer.kind === 'select') {
    const trimmed = answer.value.trim();
    return trimmed === '' ? null : trimmed;
  }
  return null;
}

/** A `select` answer's raw value — 'yes'/'no'/'unknown' for F16/F18, an M5 option elsewhere. */
function selectOf(doc: SurveyDocument, id: string, scope: Scope = {}): string | null {
  const answer = answerOf(doc, id, scope);
  return answer?.kind === 'select' ? answer.value : null;
}

/**
 * F16 / F18's `select` answer, narrowed to the three values the sheet's own
 * type promises (D-041 postscript).
 *
 * A `select` answer's value is an UNCONSTRAINED string in the schema — the
 * catalog's own option list is not a runtime check — so `selectOf(...) as
 * 'yes' | 'no' | 'unknown' | null` used to assert a shape the value might not
 * have. This reads the value back and only claims one of the three when the
 * value actually is one; anything else (a future option, a corrupted record)
 * reads `null`, same as no answer at all.
 */
function yesNoUnknownOf(
  doc: SurveyDocument,
  id: string,
  scope: Scope = {},
): 'yes' | 'no' | 'unknown' | null {
  const value = selectOf(doc, id, scope);
  return value === 'yes' || value === 'no' || value === 'unknown' ? value : null;
}

function boolOf(doc: SurveyDocument, id: string, scope: Scope = {}): boolean | null {
  const answer = answerOf(doc, id, scope);
  if (!answer) return null;
  if (answer.kind === 'bool') return answer.value;
  if (answer.kind === 'tristate') return answer.value === 'unknown' ? null : answer.value === 'yes';
  return null;
}

function numberOf(doc: SurveyDocument, id: string, scope: Scope = {}): number | null {
  const answer = answerOf(doc, id, scope);
  return answer?.kind === 'number' ? answer.value : null;
}

function picksOf(doc: SurveyDocument, id: string, scope: Scope = {}): Set<string> | null {
  const answer = answerOf(doc, id, scope);
  return answer?.kind === 'multiselect' ? new Set(answer.value) : null;
}

function contactOf(doc: SurveyDocument, id: string, scope: Scope = {}): Contact | null {
  const answer = answerOf(doc, id, scope);
  if (answer?.kind !== 'contact') return null;
  const { name, phone, email } = answer.value;
  return name || phone || email ? { name, phone, email } : null;
}

function spansOf(doc: SurveyDocument, id: string, scope: Scope = {}): LandingSpan[] {
  const answer = answerOf(doc, id, scope);
  if (answer?.kind !== 'ranges') return [];
  return answer.value.map((range: WallRange) => ({
    fromLanding: range.fromLanding,
    toLanding: range.toLanding,
  }));
}

/** Fold a column of yes/no/null rows into the grid's one word — exported for `flsVendorForm.ts`'s folds (spec §5.2), so the two blocks share the one rule. */
export function presence(rows: readonly (boolean | null)[]): Presence {
  const known = rows.filter((row): row is boolean => row !== null);
  if (known.length === 0) return 'unknown';
  const yes = known.filter(Boolean).length;
  if (yes === 0) return 'no';
  // Partly: some rows said yes and others said no, OR some said yes and the
  // rest are still blank — either way the company must not read "every one".
  return yes === rows.length ? 'yes' : 'some';
}

/**
 * Fold a column of yes/no/null rows into ONE fact, OR-style — used within a
 * single room, where "any one of these three questions said Yes" is a single
 * Yes, not a partial (spec §5.1, D-041). `true` if any row is `true`; `false`
 * if none is `true` but at least one is `false`; `null` only when every row is
 * `null` (nobody has said anything at all). Exported for `flsVendorForm.ts`'s
 * `conditions.hoistwayDevices` fold (F9 with F13), the same rule.
 */
export function anyOf(rows: readonly (boolean | null)[]): boolean | null {
  if (rows.some((row) => row === true)) return true;
  if (rows.some((row) => row === false)) return false;
  return null;
}

/* ═══════════════════════════════ building ════════════════════════════════ */

export function buildFlsCutSheet(doc: SurveyDocument): FlsCutSheet {
  const sections = buildSurveySections(doc);
  // Titles travel away from the room header here, so they carry the room
  // (qualifyTitle, as a Gap does): "Penthouse Machine Room · Hoistway 2 · Pit".
  const roomName = new Map(doc.rooms.map((room) => [room.id, room.name]));
  const titleOf = new Map(
    sections.map((section) => {
      const room = section.scope.roomId ? roomName.get(section.scope.roomId) : undefined;
      return [section.id, room ? qualifyTitle(room, section.title) : section.title];
    }),
  );
  const shotLabel = new Map<string, string>();
  // Amendment 8 (D-041): which shots are REQUIRED — keyed by BASE id, since a
  // per-car shot's `required` flag travels on the base entry, not the copy.
  const requiredShots = new Set<string>();
  for (const list of Object.values(SHOT_LISTS)) {
    for (const shot of list) {
      shotLabel.set(shot.id, shot.label);
      if (shot.required) requiredShots.add(shot.id);
    }
  }
  // Amendment 8 (D-041): a per-car shot's id carries the car, not its label —
  // this is the one map that turns `car-3` back into "Car 3" for the sheet.
  const carLabelById = new Map(doc.rooms.flatMap((room) => room.cars.map((car) => [car.id, car.label])));

  const describe = (photo: PhotoRef): CutSheetPhoto => {
    const question = photo.questionId ? byId(photo.questionId) : undefined;
    const base = photo.shotListId ? shotBaseId(photo.shotListId) : undefined;
    const carId = photo.shotListId ? shotCarId(photo.shotListId) : undefined;
    const carLabel = carId ? carLabelById.get(carId) : undefined;
    const baseLabel = base ? shotLabel.get(base) : undefined;
    // The catalog's own chain — what the surveyor was prompted with — travels
    // as `prompt` regardless of whether they typed a label of their own.
    const prompt =
      (baseLabel && (carLabel ? `${baseLabel} — ${carLabel}` : baseLabel)) ||
      question?.label ||
      titleOf.get(photo.sectionInstanceId) ||
      photo.sectionInstanceId;
    return {
      id: photo.id,
      r2Key: photo.r2Key,
      contentType: photo.contentType,
      bytes: photo.bytes,
      capturedAt: photo.capturedAt,
      uploadState: photo.uploadState,
      sectionInstanceId: photo.sectionInstanceId,
      sectionTitle: titleOf.get(photo.sectionInstanceId) ?? photo.sectionInstanceId,
      ...(photo.questionId ? { questionId: photo.questionId } : {}),
      ...(photo.shotListId ? { shotListId: photo.shotListId } : {}),
      // The surveyor's own words first (D-038); the catalog prompt only when
      // they never labelled this photo — the sheet's own D-016 rule, not
      // invented on their behalf.
      label: photo.label ?? prompt,
      prompt,
      ...(carId && carLabel ? { carId, carLabel } : {}),
      ...(base && requiredShots.has(base) ? { required: true as const } : {}),
    };
  };

  const taken = new Set<string>();
  const photosWhere = (keep: (photo: PhotoRef) => boolean): CutSheetPhoto[] =>
    doc.photos
      .filter((photo) => !taken.has(photo.id) && keep(photo))
      .sort((a, b) => a.capturedAt.localeCompare(b.capturedAt))
      .map((photo) => {
        taken.add(photo.id);
        return describe(photo);
      });

  const flsQuestionPhoto = (photo: PhotoRef): boolean =>
    photo.questionId !== undefined && (isFlsQuestionId(photo.questionId) || photo.questionId === 'M4');

  /* ── the panel (F1–F5, survey level) ────────────────────────────────────── */
  const flsSection = sectionInstanceId('fls');
  const buildingSection = sectionInstanceId('building');
  const panelPhotos = photosWhere(
    (photo) =>
      photo.sectionInstanceId === flsSection ||
      // The FACP and its sticker were prompted on the building before Fire Life
      // Safety was a section of its own (RecommendedShot.formerly); the same
      // picture, by a surveyor standing in front of the panel.
      (photo.sectionInstanceId === buildingSection &&
        (photo.shotListId === 'building.facp' ||
          photo.shotListId === 'building.facp_sticker' ||
          flsQuestionPhoto(photo))),
  );

  const typedBackBoxes = numberOf(doc, 'F1');
  const derivedBackBoxes = flsBackBoxCount(doc.rooms);

  /* ── machine rooms (M4, F6, F7, F8, F14 — Electrical) ──────────────────── */
  const machineRooms: CutSheetRoom[] = doc.rooms.map((room) => {
    const scope: Scope = { roomId: room.id };
    const relayPicks = picksOf(doc, 'F8', scope);
    const devicePicks = picksOf(doc, 'F14', scope);
    const electrical = sectionInstanceId('electrical', scope);
    const setup = sectionInstanceId('room_setup', scope);
    const general = sectionInstanceId('general', scope);
    const cab = sectionInstanceId('cab', scope);
    return {
      id: room.id,
      name: room.name,
      type: room.type,
      cars: room.cars.map((car) => car.label),
      sprinklered: boolOf(doc, 'M4', scope),
      heatDetectorAtSprinklerHead: boolOf(doc, 'F6', scope),
      shuntRelay: boolOf(doc, 'F7', scope),
      relays: relayPicks
        ? {
            primary: relayPicks.has('primary'),
            alternate: relayPicks.has('secondary'),
            flashingHat: relayPicks.has('flashing_hat'),
            shunt: relayPicks.has('shunt'),
          }
        : null,
      devices: devicePicks ? { smoke: devicePicks.has('smoke'), heat: devicePicks.has('heat') } : null,
      location: selectOf(doc, 'M5', scope),
      facpDistanceFt: numberOf(doc, 'F15', scope),
      recallInstalled: yesNoUnknownOf(doc, 'F16', scope),
      shuntDisconnectLocation: textOf(doc, 'F20', scope),
      mainDisconnectFeed: textOf(doc, 'F21', scope),
      // Amendment 8 (D-041) gathers the wide room, door, controller and
      // disconnects shots onto the sheet, on top of what already landed here.
      photos: photosWhere(
        (photo) =>
          (photo.sectionInstanceId === electrical ||
            photo.sectionInstanceId === setup ||
            photo.sectionInstanceId === general) &&
          (photo.shotListId === 'electrical.fls_relays' ||
            photo.shotListId === 'electrical.disconnects' ||
            photo.shotListId === 'general.controller' ||
            photo.shotListId === 'general.controller_inside' ||
            photo.shotListId === 'general.wide_room' ||
            photo.shotListId === 'general.door' ||
            flsQuestionPhoto(photo)),
      ),
      // Amendment 8 (D-041): the cab section's two shots are per car — the COP
      // and the posted certificate — so every car gets its own row, empty or
      // not, rather than one pile the company has to sort out itself.
      carPhotos: room.cars.map((car) => ({
        carId: car.id,
        carLabel: car.label,
        photos: photosWhere(
          (photo) =>
            photo.sectionInstanceId === cab &&
            photo.shotListId !== undefined &&
            shotCarId(photo.shotListId) === car.id,
        ),
      })),
    };
  });

  /* ── hoistways (F9, F13 — Overhead; F10 — Pit) ─────────────────────────── */
  const hoistways: CutSheetHoistway[] = doc.rooms.flatMap((room) =>
    room.hoistways.map((hoistway): CutSheetHoistway => {
      const scope: Scope = { roomId: room.id, hoistwayId: hoistway.id };
      const overheadPicks = picksOf(doc, 'F13', scope);
      const pitPicks = picksOf(doc, 'F10', scope);
      const overhead = sectionInstanceId('overhead', scope);
      const pit = sectionInstanceId('pit', scope);
      const carLabel = new Map(room.cars.map((car) => [car.id, car.label]));
      return {
        id: hoistway.id,
        name: hoistway.name,
        roomId: room.id,
        roomName: room.name,
        cars: hoistway.carIds.map((id) => carLabel.get(id) ?? id),
        overhead: {
          sprinklered: boolOf(doc, 'F9', scope),
          devices: overheadPicks
            ? { smoke: overheadPicks.has('smoke'), heat: overheadPicks.has('heat') }
            : null,
        },
        pit: {
          devices: pitPicks
            ? {
                sprinkler: pitPicks.has('sprinkler'),
                heat: pitPicks.has('heat'),
                smoke: pitPicks.has('smoke'),
              }
            : null,
        },
        photos: photosWhere(
          (photo) =>
            (photo.sectionInstanceId === overhead || photo.sectionInstanceId === pit) &&
            // The wide shots of the shaft top and the pit floor are where the
            // detectors and heads are; the company reads them for exactly that.
            (photo.shotListId === 'overhead.top_of_hoistway' ||
              photo.shotListId === 'pit.full_pit' ||
              flsQuestionPhoto(photo)),
        ),
      };
    }),
  );

  /* ── lobby groups (F11/F11a, F12/F12a/F12b — Lobbies) ──────────────────── */
  const lobbyGroups: CutSheetLobbyGroup[] = doc.rooms.flatMap((room) =>
    room.groups.map((group): CutSheetLobbyGroup => {
      const scope: Scope = { roomId: room.id, groupId: group.id };
      const lobby = sectionInstanceId('lobby', scope);
      const carLabel = new Map(room.cars.map((car) => [car.id, car.label]));
      const detectorPicks = picksOf(doc, 'F17', scope);
      return {
        id: group.id,
        name: group.name,
        roomId: room.id,
        roomName: room.name,
        cars: group.carIds.map((id) => carLabel.get(id) ?? id),
        landings: group.landings,
        smokeDetectorsMissing: boolOf(doc, 'F11', scope),
        landingsMissingSmoke: spansOf(doc, 'F11a', scope),
        sprinklered: boolOf(doc, 'F12', scope),
        sprinkleredLandings: spansOf(doc, 'F12a', scope),
        sprinkleredLobbiesHaveHeat: boolOf(doc, 'F12b', scope),
        detectorTypes: detectorPicks
          ? {
              smoke: detectorPicks.has('smoke'),
              heat: detectorPicks.has('heat'),
              other: detectorPicks.has('other'),
            }
          : null,
        photos: photosWhere(
          (photo) =>
            photo.sectionInstanceId === lobby &&
            (photo.shotListId === 'lobby.detector_egress' ||
              photo.shotListId === 'lobby.detector_typical' ||
              flsQuestionPhoto(photo)),
        ),
      };
    }),
  );

  /* ── the grid ───────────────────────────────────────────────────────────── */
  const roomDevices = machineRooms.map((room) => room.devices);
  const overheadDevices = hoistways.map((hoistway) => hoistway.overhead.devices);
  const pitDevices = hoistways.map((hoistway) => hoistway.pit.devices);

  // The one shunt-relay row a room actually has — F8's Shunt box when the
  // relay inventory was answered at all, else the plain F7 yes/no. Shared by
  // `relays.shunt` (below) and `existingIn` (spec §5.1), so the two can never
  // read the same room two different ways.
  const roomShunt = (room: CutSheetRoom): boolean | null =>
    room.relays ? room.relays.shunt : room.shuntRelay;

  // spec §5.1: a shunt is REQUIRED wherever code says there is a sprinkler to
  // shunt — the room itself (M4), its hoistways' overhead (F9), or a
  // sprinkler ticked in one of its pits (F10) — any one Yes is enough, not a
  // fraction of the room's own rows.
  const requiredIn = (room: CutSheetRoom): boolean | null => {
    const roomHoistways = hoistways.filter((hoistway) => hoistway.roomId === room.id);
    return anyOf([
      room.sprinklered,
      ...roomHoistways.map((hoistway) => hoistway.overhead.sprinklered),
      ...roomHoistways.map((hoistway) => hoistway.pit.devices?.sprinkler ?? null),
    ]);
  };

  // spec §5.1: a shunt EXISTS when the relay inventory says so, OR when E4c1
  // says the disconnects being refurbished already carry one — a shunt found
  // on a disconnect is a shunt that exists, whichever question found it.
  const existingIn = (room: CutSheetRoom): boolean | null =>
    anyOf([roomShunt(room), boolOf(doc, 'E4c1', { roomId: room.id })]);

  const summary: FlsCutSheet['summary'] = {
    sprinklerSystem: yesNoUnknownOf(doc, 'F18'),
    lobbies: {
      // F11 asks the question the other way round: "missing a smoke detector?"
      // No missing = every landing has one.
      smoke: presence(
        lobbyGroups.map((group) =>
          group.smokeDetectorsMissing === null ? null : !group.smokeDetectorsMissing,
        ),
      ),
      heat: presence(lobbyGroups.map((group) => group.sprinkleredLobbiesHaveHeat)),
      sprinkled: presence(lobbyGroups.map((group) => group.sprinklered)),
    },
    pit: {
      smoke: presence(pitDevices.map((devices) => (devices ? devices.smoke : null))),
      heat: presence(pitDevices.map((devices) => (devices ? devices.heat : null))),
      sprinkled: presence(pitDevices.map((devices) => (devices ? devices.sprinkler : null))),
    },
    overhead: {
      smoke: presence(overheadDevices.map((devices) => (devices ? devices.smoke : null))),
      heat: presence(overheadDevices.map((devices) => (devices ? devices.heat : null))),
      sprinkled: presence(hoistways.map((hoistway) => hoistway.overhead.sprinklered)),
    },
    machineRoom: {
      smoke: presence(roomDevices.map((devices) => (devices ? devices.smoke : null))),
      heat: presence(roomDevices.map((devices) => (devices ? devices.heat : null))),
      sprinkled: presence(machineRooms.map((room) => room.sprinklered)),
    },
    relays: {
      primary: presence(machineRooms.map((room) => room.relays?.primary ?? null)),
      alternate: presence(machineRooms.map((room) => room.relays?.alternate ?? null)),
      flashingHat: presence(machineRooms.map((room) => room.relays?.flashingHat ?? null)),
      shunt: presence(machineRooms.map(roomShunt)),
    },
    // Amendment 8 (D-041), spec §5.1: required folds the room, its hoistways'
    // overhead and its pits' sprinkler pick (`requiredIn`); existing folds the
    // relay inventory and E4c1's refurbished-disconnect shunts (`existingIn`)
    // — a wider read than `relays.shunt` above, which is F7/F8 alone.
    shunt: {
      required: presence(machineRooms.map(requiredIn)),
      existing: presence(machineRooms.map(existingIn)),
    },
  };

  /* ── notes and blanks ───────────────────────────────────────────────────── */
  const notes: CutSheetNote[] = doc.notes
    .filter(
      (note) =>
        // Amendment 8 (D-041): a shot note is the written reason on a required
        // photo's blank, not a job note — it travels in `open[]` instead.
        note.shotListId === undefined &&
        (note.sectionInstanceId === flsSection ||
          (note.questionId !== undefined && isFlsQuestionId(note.questionId))),
    )
    .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    .map((note) => ({
      sectionTitle: titleOf.get(note.sectionInstanceId) ?? note.sectionInstanceId,
      ...(note.questionId ? { questionId: note.questionId } : {}),
      text: note.text,
      createdAt: note.createdAt,
    }));

  const open: CutSheetOpenItem[] = [];
  for (const section of sections) {
    for (const question of flsQuestionsIn(section.kind)) {
      if (!isQuestionVisible(doc, question.id, section.scope)) continue;
      if (answerOf(doc, question.id, section.scope) !== undefined) continue;
      open.push({
        questionId: question.id,
        label: question.label,
        sectionInstanceId: section.id,
        sectionTitle: titleOf.get(section.id) ?? section.title,
      });
    }
  }

  // Amendment 8 (D-041): a REQUIRED shot nobody took is a blank on the sheet
  // too — the surveyor's written reason for skipping it (the shot note, not a
  // job note) travels here as `reason`, same role D-017's acknowledgement had.
  for (const shot of missingShots(doc)) {
    if (!shot.required) continue;
    const reason = shotReasonFor(doc, shot.sectionInstanceId, shot.shotListId);
    open.push({
      shotListId: shot.shotListId,
      label: shot.shotLabel,
      sectionInstanceId: shot.sectionInstanceId,
      sectionTitle: shot.sectionTitle,
      ...(reason ? { reason } : {}),
    });
  }

  const allPhotos = [
    ...panelPhotos,
    ...machineRooms.flatMap((room) => [...room.photos, ...room.carPhotos.flatMap((car) => car.photos)]),
    ...hoistways.flatMap((hoistway) => hoistway.photos),
    ...lobbyGroups.flatMap((group) => group.photos),
  ];

  // The address in four parts (D-039): the sheet prints the composed line and
  // carries the parts beside it, null where the record has none.
  const address = addressPartsOf(doc);

  // Built without `vendorForm` first — Task 7's builder takes exactly this
  // shape, so it cannot read the very member it is producing.
  const sheetSoFar: Omit<FlsCutSheet, 'vendorForm'> = {
    template: 'fls-turnkey-rfq',
    version: FLS_CUT_SHEET_VERSION,
    job: {
      name: textOf(doc, 'B1'),
      surveyor: doc.submittedByName ?? doc.assignedTo ?? null,
      address: formatAddress(address) || null,
      city: address.city || null,
      state: address.state || null,
      zip: address.zip || null,
      buildingType: textOf(doc, 'B3'),
      date: doc.submittedAt ?? doc.updatedAt,
      elevators: numberOf(doc, 'B11'),
      landings: derivedBackBoxes,
      machineRooms: doc.rooms.length,
      hoistways: hoistways.length,
      groups: lobbyGroups.length,
    },
    panel: {
      makeModel: textOf(doc, 'F3'),
      serviceProvider: textOf(doc, 'F4'),
      serviceContact: contactOf(doc, 'F5'),
      backBoxes: typedBackBoxes ?? (derivedBackBoxes > 0 ? derivedBackBoxes : null),
      notes: textOf(doc, 'F2'),
      access: textOf(doc, 'F19'),
      photos: panelPhotos,
    },
    summary,
    machineRooms,
    hoistways,
    lobbyGroups,
    photos: allPhotos,
    notes,
    open,
  };

  return { ...sheetSoFar, vendorForm: buildVendorForm(sheetSoFar, doc) };
}

/** The FLS questions the catalog files under a section kind, in catalog order. */
function flsQuestionsIn(kind: string): readonly { id: string; label: string }[] {
  return FLS_QUESTIONS_BY_SECTION.get(kind) ?? [];
}

const FLS_QUESTIONS_BY_SECTION: Map<string, { id: string; label: string }[]> = (() => {
  const map = new Map<string, { id: string; label: string }[]>();
  for (const question of QUESTIONS) {
    if (!isFlsQuestionId(question.id)) continue;
    // F1 is counted by the form, never asked (catalog `auto`); it cannot be a blank.
    if (question.auto) continue;
    const list = map.get(question.section) ?? [];
    list.push({ id: question.id, label: question.label });
    map.set(question.section, list);
  }
  return map;
})();
